import { Routes } from '@angular/router';
import { PublicLayoutComponent } from './layouts/public-layout/public-layout.component';
import { environment } from '../environments/environment';
import { Commons } from './shared/Commons';
import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout.component';
import { customerGuard } from './guard/customer.guard';
import { flowAdminGuard } from './guard/flow-admin.guard';
import { flowPublicationsGuard } from './guard/flow-publications.guard';
import { flowQuotesGuard } from './guard/flow-quotes.guard';
import { CustomMetaData } from './shared/CustomMetaData';
import { landingPageMatcher } from './shared/landing-page.matcher';

export const routes: Routes = [

  // 1️⃣ rutas públicas
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      /* ---------- rutas siempre presentes ---------- */
      {
        path: Commons.PATH_MAIN,
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/public/home/home.component').then((m) => m.HomeComponent),
        data: { seo: CustomMetaData.HOME }
      },
      {
        path: Commons.PATH_LOGIN,
        loadComponent: () =>
          import('./pages/public/login/login.component').then((m) => m.LoginComponent),
      },
      {
        path: Commons.PATH_PWD_REC,
        loadComponent: () =>
          import('./pages/public/login/password-recovery/password-recovery.component').then((m) => m.PasswordRecoveryComponent),
      },
      {
        path: `${Commons.PATH_PWD_CHN}/:transientAuth/:lang`,
        loadComponent: () =>
          import('./pages/public/login/password-change/password-change.component').then((m) => m.PasswordChangeComponent),
      },
      {
        path: Commons.PATH_REGISTER,
        loadComponent: () =>
          import('./pages/public/login/register/register.component').then((m) => m.RegisterComponent),
      },
      {
        path: `${Commons.PATH_MAIL_VER}/:transientAuth/:lang`,
        loadComponent: () =>
          import('./pages/public/login/register/verify-email/verify-email.component').then((m) => m.VerifyEmailComponent),
      },
      {
        path: `${Commons.PATH_PUBLIC_QUOTE}/:ownerId/:quoteId/:code`,
        loadComponent: () =>
          import('./pages/public/view-quote/view-quote.component').then((m) => m.ViewQuoteComponent),
      },
      {
        path: `${Commons.PATH_TERMS}/:code`,
        loadComponent: () =>
          import('./pages/public/terms/terms.component').then((m) => m.TermsComponent),
      },
      {
        path: Commons.PATH_MY_CUSTOMER,
        loadComponent: () =>
          import('./pages/public/profile/profile.component').then((m) => m.ProfileComponent),
      },
      /* ---------- BEGIN rutas custom ---------- */
      {
        path: 'status',
        loadComponent: () =>
          import('./pages/public/custom-components/service-status/service-status.component').then((m) => m.ServiceStatusComponent),
      },
      {
        path: 'docs',
        loadComponent: () =>
          import('./pages/public/custom-components/docs/docs.component').then((m) => m.DocsComponent),
      },
      /* ---------- END rutas custom ---------- */

      /* ---------- rutas condicionales ---------- */
      ...(
        environment.home.services === 'page'
          ?
          [
            {
              path: Commons.PATH_SERVICES,
              loadComponent: () =>
                import('./pages/public/services/services.component')
                  .then(m => m.ServicesComponent),
              data: { seo: CustomMetaData.SERVICES }
            }
          ]
          : []
      ),
      /* ---------- BEGIN SERVICES custom routes ---------- */
      {
        path: `${Commons.PATH_SERVICES}/service-1`,
        loadComponent: () =>
          import('./pages/public/services/custom/service-1/service-1.component')
            .then(m => m.Service1Component),
        data: { seo: CustomMetaData.SERVICE_1 }
      },
      {
        path: `${Commons.PATH_SERVICES}/service-2`,
        loadComponent: () =>
          import('./pages/public/services/custom/service-2/service-2.component')
            .then(m => m.Service2Component),
        data: { seo: CustomMetaData.SERVICE_2 }
      },
      {
        path: `${Commons.PATH_SERVICES}/service-3`,
        loadComponent: () =>
          import('./pages/public/services/custom/service-3/service-3.component')
            .then(m => m.Service3Component),
        data: { seo: CustomMetaData.SERVICE_3 }
      },
      /*
      {
        path: `${Commons.PATH_SERVICES}/service-n`,
        loadComponent: () =>
          import('./pages/public/services/custom/service-n/service-n.component')
            .then(m => m.ServiceNComponent),
        data: { seo: CustomMetaData.SERVICES }
      },
      */
      /* ---------- END SERVICES custom routes ---------- */
      ...(
        environment.home.about_us === 'page'
          ?
          [
            {
              path: Commons.PATH_ABOUT,
              loadComponent: () =>
                import('./pages/public/about/about.component')
                  .then(m => m.AboutComponent),
              data: { seo: CustomMetaData.ABOUT }
            }
          ]
          : []
      ),

      ...(
        environment.home.contact === 'page'
          ?
          [
            {
              path: Commons.PATH_CONTACT,
              loadComponent: () =>
                import('./pages/public/contact/contact.component')
                  .then(m => m.ContactComponent),
              data: { seo: CustomMetaData.CONTACT }
            }
          ]
          : []
      ),

      ...(
        environment.home.team === 'page'
          ?
          [
            {
              path: Commons.PATH_TEAM,
              loadComponent: () =>
                import('./pages/public/team/team.component')
                  .then(m => m.TeamComponent),
              data: { seo: CustomMetaData.TEAM }
            }
          ]
          : []
      ),

      ...(
        environment.home.map === 'page'
          ?
          [
            {
              path: Commons.PATH_LOCATION,
              loadComponent: () =>
                import('./pages/public/map/map.component')
                  .then(m => m.MapComponent),
              data: { seo: CustomMetaData.LOCATION }
            }
          ]
          : []
      ),

      ...(
        environment.home.publications === 'page'
          ?
          [
            {
              path: 'publications',
              loadComponent: () =>
                import('./pages/publications/publications.component')
                  .then(m => m.PublicationsComponent),
              data: { seoDynamic: true }
            },
            {
              path: 'publications/:slugId',
              loadComponent: () =>
                import('./pages/publications/publications.component')
                  .then(m => m.PublicationsComponent),
              data: { seoDynamic: true }
            }
          ]
          : []
      ),

      ...(
        environment.home.gallery === 'page'
          ?
          [
            {
              path: Commons.PATH_GALLERY,
              loadComponent: () =>
                import('./pages/public/gallery/gallery.component')
                  .then(m => m.GalleryComponent),
              data: { seo: CustomMetaData.GALLERY }
            }
          ]
          : []
      ),

      /* ---------- landing pages SEO dinámicas (ver CustomLandingPages.ts) ---------- */
      /* Debe ir última: cualquier ruta fija de arriba tiene prioridad sobre un slug de cliente. */
      {
        matcher: landingPageMatcher,
        loadComponent: () =>
          import('./pages/public/landing/landing.component').then((m) => m.LandingComponent),
        data: { seoDynamic: true }
      },
    ]
  },
  // 2️⃣ zona ADMIN protegida
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        canActivate: [customerGuard],
        path: Commons.PATH_PRODUCT,
        loadComponent: () =>
          import('./pages/admin/admin-home/admin-home.component')
            .then(m => m.AdminHomeComponent)
      },
      {
        canActivate: [flowAdminGuard],
        path: Commons.PATH_CONFIG_WITH_LIC,
        loadComponent: () =>
          import('./pages/admin/configuration/configuration.component')
            .then(m => m.ConfigurationComponent)
      },
      {
        canActivate: [flowPublicationsGuard],
        path: Commons.PATH_PUBLICATIONS_WITH_LIC,
        loadComponent: () =>
          import('./pages/admin/publications/publications.component')
            .then(m => m.PublicationsComponent)
      },
      {
        canActivate: [flowPublicationsGuard],
        path: `${Commons.PATH_PUBLICATIONS_WITH_LIC}/editor`,
        loadComponent: () =>
          import('./pages/admin/publications/new-post/new-post.component')
            .then(m => m.NewPostComponent)
      },
      {
        canActivate: [flowQuotesGuard],
        path: Commons.PATH_QUOTES,
        loadComponent: () =>
          import('./pages/admin/quotes/quotes.component')
            .then(m => m.QuotesComponent)
      },
      {
        canActivate: [flowQuotesGuard],
        path: `${Commons.PATH_QUOTES}/quote`,
        loadComponent: () =>
          import('./pages/admin/quotes/quote/quote.component')
            .then(m => m.QuoteComponent)
      },
      {
        canActivate: [flowAdminGuard],
        path: Commons.PATH_CLIENTS,
        loadComponent: () =>
          import('./pages/admin/clients/clients.component')
            .then(m => m.ClientsComponent)
      },
      {
        canActivate: [flowAdminGuard],
        path: `${Commons.PATH_CLIENTS}/client`,
        loadComponent: () =>
          import('./pages/admin/clients/client/client.component')
            .then(m => m.ClientComponent)
      },

      /* ---------- panel interno de desarrollo, nunca en producción ---------- */
      ...(
        !environment.production
          ?
          [
            {
              canActivate: [customerGuard],
              path: Commons.PATH_ADMIN_UI_KIT,
              loadComponent: () =>
                import('./pages/admin/admin-ui-kit/showcase/admin-ui-kit-showcase.component')
                  .then(m => m.AdminUiKitShowcaseComponent)
            }
          ]
          : []
      ),
    ]
  },

  {
    path: '**',
    redirectTo: Commons.PATH_MAIN
  }
];
